/* eslint-disable no-nested-ternary, max-lines */
import React, { Dispatch, useEffect, useMemo, useState, useImperativeHandle } from "react";
import { Card, Accordion, Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { useWatch, useFormContext, get } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import { FormattedMessage, useIntl } from "react-intl";
import ReactHtmlParser from "react-html-parser";
import PropTypes from "prop-types";

import { ReactComponent as SortIcon } from "assets/images/icons/SortDown.svg";
import { ReactComponent as CheckIcon } from "assets/images/icons/Check.svg";
import { ReactComponent as ExclamationIcon } from "assets/images/icons/Exclamation.svg";
import { Ilocations } from "types/Common";
import { requestFormStartingPoint, getPaymentLocationFormDetails } from "api";
import { getLabelWithTranslationFallback } from "helpers/getLabel";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import { IDropdownProps, IFieldProps } from "types/Organisational";
import Textarea from "components/atoms/Textarea/Textarea";
import Alert from "components/molecules/Alert/Alert";
import { getObjectByKeys } from "helpers/commonHelpers";
import { orderOrganizationalFields } from "helpers/requestFormHelper";
import { extractAllLinks, replaceLinks } from "helpers/urlHelpers";
import { FormStepTypes, FormStepValidationTypes } from "constants/";
import testIds from "constants/testIds";
import FormHeader from "components/v2/FormSection/components/FormHeader/FormHeader";
import FormSection from "components/v2/FormSection/FormSection";
import SearchSelect from "../Common/SearchSelect";
import { RequestFormAction, RequestFormActionType, RequestFormState } from "../../RequestFormState";
import FormItemInfo from "../Common/FormItemInfo";
import OrganisationFormFreightMode from "./OrganisationFormFreightMode";
import FormHr from "../Common/FormHr";
import "./organisationForm.scss";
import OrganisationFormField from "./OrganisationFormField";

type OrganisationFormProps = {
  state: RequestFormState;
  dispatch: Dispatch<RequestFormAction>;
  isQuoteRequest: boolean;
  childRef?: React.MutableRefObject<any>;
  isMiniTender?: boolean;
  validateSection?: Function;
  shouldValidateSection?: React.MutableRefObject<boolean>;
  isNewShipment?: boolean;
  isLoadingShipmentBasic?: boolean;
  isConsolidated?: boolean;
  isShipment?: boolean;
  disablePaymentLocation?: boolean;
};

const sepratedRenderedFields = ["invoice_location", "custom_long_field_01"];
function OrganisationForm(props: OrganisationFormProps) {
  const {
    state,
    dispatch,
    isQuoteRequest,
    childRef,
    isMiniTender,
    validateSection,
    shouldValidateSection,
    isNewShipment,
    isLoadingShipmentBasic,
    isConsolidated,
    isShipment,
    disablePaymentLocation,
  } = props;
  const { locationDetails } = state;
  const intl = useIntl();
  const {
    getValues,
    setError,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const [paymentLocations, setPaymentLocations] = useState<Ilocations[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [organizationalOptions, setOrganizationalOptions] = useState<any>([]);

  const [location, reasonCode, costRecoveryRequired] = useWatch({
    name: ["organizational.location", "organizational.reason_code", "organizational.cost_recovery_required"],
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const currentActiveStep = searchParams.get("active_step");
  const draftId = searchParams.get("draft_id");
  const organisationFields = FormStepValidationTypes[FormStepTypes.ORGANISATION] as string[];
  const hasError = organisationFields.some((item) => get(errors, item));
  const { getPaymentLocationLabel } = usePermissions();

  const showCostRecovery = useMemo(() => {
    if (locationDetails?.organizational_options?.cost_recovery_required?.mandatory) return true;
    const reasonCodeData = locationDetails?.organizational_options?.reason_code;
    if (reasonCode && reasonCodeData && reasonCodeData?.dropdown_values?.length) {
      const selectedReasonCode = reasonCodeData.dropdown_values.find((item: any) => item.value === reasonCode);
      return selectedReasonCode && selectedReasonCode.cost_recovery_possible;
    }
    return false;
  }, [locationDetails, reasonCode]);

  useImperativeHandle(childRef, () => ({ checkStepValidation }));

  useEffect(() => {
    requestFormStartingPoint()
      .then((res) => {
        const locations = res.data.locations.filter((location: Ilocations) => location.available);
        locations?.sort((a: Ilocations, b: Ilocations) => a?.label?.localeCompare(b.label));
        setPaymentLocations(locations);
        dispatch({ type: RequestFormActionType.PAYMENT_LOCATION_SUCCESS, payload: getObjectByKeys(locations, "value") });
      })
      .catch((err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "general.couldNotRetrievePaymentLocation",
            defaultMessage: "Could not retrieve payment locations. Please try again later.",
          }),
        ),
      );
  }, []);

  /**
   * If cost recovery is not required, we need to clear the custom cost recovery fields; backend requires them to be present even if cost recovery is not required
   */
  useEffect(() => {
    if (costRecoveryRequired === "False") {
      if (getValues("organizational.custom_cost_recovery_field1") !== "")
        setValue("organizational.custom_cost_recovery_field1", "");
      if (getValues("organizational.custom_cost_recovery_field2") !== "")
        setValue("organizational.custom_cost_recovery_field2", "");
    }
  }, [costRecoveryRequired]);

  useEffect(() => {
    if (!location) return;
    setIsLoading(true);

    getPaymentLocationFormDetails(
      location,
      {
        params: {
          location,
          ...(isMiniTender && { is_mini_tender: true }),
          ...(isQuoteRequest && { shipment_draft_reference: draftId }),
          ...(!isQuoteRequest && { quick_request_draft_reference: draftId }),
        },
      },
      isQuoteRequest,
    )
      .then((res) => {
        if (!draftId && !isConsolidated) {
          searchParams.set("draft_id", res.data.shipment_draft_reference);
          setSearchParams(searchParams);
        }
        const updatedData: any = { ...res.data };
        updatedData.organizational_options = res.data.organizational_options.map((item: any) => {
          if (item.dropdown_values && Array.isArray(item.dropdown_values) && item.dropdown_values.length) {
            return {
              ...item,
              dropdown_values: item.dropdown_values
                .map((dropdownVal: any) => ({
                  ...dropdownVal,
                  label: dropdownVal.key,
                }))
                .sort((a: IDropdownProps, b: IDropdownProps) => a?.key?.localeCompare(b.key)),
            };
          }

          return item;
        });
        setOrganizationalOptions(orderOrganizationalFields(updatedData.organizational_options, updatedData.order));
        updatedData.organizational_options = getObjectByKeys(updatedData.organizational_options, "field_id");
        dispatch({ type: RequestFormActionType.LOCATION_DETAIL_SUCCESS, payload: updatedData });

        if (shouldValidateSection && validateSection && shouldValidateSection.current) {
          setTimeout(() => {
            validateSection(FormStepTypes.ORGANISATION);
          }, 200);
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [location, isQuoteRequest, isMiniTender]);

  const checkStepValidation = () => {
    let shouldInvalidate = isQuoteRequest;
    const invitedModes = getValues("invited_modes");
    if (invitedModes) {
      Object.keys(invitedModes).forEach((key: any) => {
        if (typeof invitedModes[key] === "boolean" && !key.includes("custom") && invitedModes[key]) shouldInvalidate = false;
        if (key === "custom" && Array.isArray(invitedModes[key])) {
          invitedModes[key].forEach((item: any) => {
            if (item.value) shouldInvalidate = false;
          });
        }
      });
    }
    if (shouldInvalidate) {
      setError("invited_modes", {
        type: "custom",
        message: intl.formatMessage({
          id: "new-request.provider.selectFreightMode",
          defaultMessage: "Please select at least one freight mode",
        }),
      });
      return false;
    }
    return true;
  };

  const renderOrganisationSection = () => (
    <div data-testid={testIds.REQUEST_FORM_ORGANISATION_SECTION}>
      <div className="flex-row organisation-form-row">
        <div className="w-200 flex-shrink-0">
          <FormItemInfo
            labelId="new-request.organisational-details.locationDetails"
            label="Location details"
            descId="new-request.organisational-details.locationDetailsInfo"
            description="Provide your location details"
          />
        </div>
        <div className="organisation-input">
          <div className="w-192">
            {!paymentLocations.length ? (
              <div className="skeleton-card">
                <Row className="skeleton-row">
                  <span key={6} className="skeleton full" />
                </Row>
              </div>
            ) : (
              <div className="w-192 mr-8">
                <SearchSelect
                  options={paymentLocations}
                  name="organizational.location"
                  testId={testIds.REQUEST_FORM_ORGANISATION_PAYMENT_LOCATION}
                  required
                  label={getLabelWithTranslationFallback("Payment location", {
                    translation: <FormattedMessage id="general.paymentLocation" defaultMessage="Payment location" />,
                  })}
                  placeholder={intl.formatMessage({
                    id: "new-request.organisational.chooseCompany",
                    defaultMessage: "Choose payment location...",
                  })}
                  disable={disablePaymentLocation}
                />
              </div>
            )}
          </div>

          {location && locationDetails?.organizational_options?.invoice_location && (
            <div className="w-192 mr-8 payment-location-input">
              <SearchSelect
                options={locationDetails.organizational_options.invoice_location.dropdown_values}
                name="organizational.invoice_location"
                testId={testIds.REQUEST_FORM_ORGANISATION_INVOICE_LOCATION}
                required={locationDetails.organizational_options.invoice_location.mandatory}
                label={
                  <>
                    {locationDetails.organizational_options.invoice_location.label}
                    <span>
                      {locationDetails.organizational_options.invoice_location.additional_information && (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="">
                              <span className="font-weight-normal">
                                {locationDetails.organizational_options.invoice_location.additional_information}
                              </span>
                            </Tooltip>
                          }
                        >
                          <FontAwesomeIcon icon={faQuestionCircle} className="font-12 text-primary ml-2" />
                        </OverlayTrigger>
                      )}
                    </span>
                  </>
                }
                placeholder={locationDetails.organizational_options.invoice_location.placeholder}
              />
            </div>
          )}
        </div>
      </div>
      <FormHr margin={`${isConsolidated ? "3xl" : "xl"}`} />
      <div className={`${isConsolidated && "mt-3xl"}`}>
        {!location && (
          <Alert variant="warning">
            <FormattedMessage
              id="new-request.organisational-details.selectPaymentLocationPrompt"
              defaultMessage="Please select a {paymentLocation} before continuing"
              values={{ paymentLocation: getPaymentLocationLabel() }}
            />
          </Alert>
        )}
      </div>
      {isLoading && location && (
        <Row>
          <Col className="pl-4">
            <div className="skeleton-card">
              <div>
                <Row className="skeleton-row">
                  <span key={1} className="skeleton" />
                  <span key={2} className="skeleton" />
                  <span key={3} className="skeleton" />
                  <span key={4} className="skeleton" />
                  <span key={5} className="skeleton" />
                  <span key={6} className="skeleton" />
                </Row>
              </div>
            </div>
          </Col>
        </Row>
      )}
      {!!organizationalOptions?.length && location && !isLoading && (
        <div className="flex-row mid-wrapper mt-24 mb-24 organisation-form-row">
          <div className="w-200 flex-shrink-0">
            <FormItemInfo
              labelId="new-request.organisational-details.shipmentDetails"
              label="Shipment details"
              descId="new-request.organisational-details.shipmentDetailsInfo"
              description="Provide your shipment details"
            />
          </div>
          <div>
            <div className="organisation-input">
              {organizationalOptions.map((field: IFieldProps) => {
                const {
                  additional_information,
                  label,
                  field_id,
                  type,
                  dropdown_values,
                  mandatory,
                  placeholder,
                  initial_data,
                } = field;

                if (
                  ((field_id === "cost_recovery_required" ||
                    field_id === "custom_cost_recovery_field1" ||
                    field_id === "custom_cost_recovery_field2") &&
                    !showCostRecovery) ||
                  sepratedRenderedFields.includes(field_id)
                ) {
                  return null;
                }

                if (Object.keys(field).length) {
                  return (
                    <OrganisationFormField
                      key={field_id}
                      additional_information={additional_information}
                      label={label}
                      field_id={field_id}
                      type={type}
                      dropdown_values={dropdown_values}
                      mandatory={mandatory}
                      placeholder={placeholder}
                      initial_data={initial_data}
                      dispatch={dispatch}
                      state={state}
                      isQuoteRequest={isQuoteRequest}
                      isConsolidated={isConsolidated}
                    />
                  );
                }
                return null;
              })}
            </div>
          </div>
        </div>
      )}
      {location && (
        <>
          {isQuoteRequest && !isShipment && (
            <>
              <FormHr margin={`${isConsolidated ? "3xl" : "xl"}`} />
              <OrganisationFormFreightMode
                state={state}
                dispatch={dispatch}
                isNewShipment={isNewShipment}
                isLoadingShipmentBasic={isLoadingShipmentBasic}
                isConsolidated={isConsolidated}
              />
            </>
          )}
          {locationDetails?.organizational_options?.custom_long_field_01 && (
            <>
              <FormHr margin={`${isConsolidated ? "3xl" : "xl"}`} />
              <div className="flex-row mt-24 mb-24 organisation-form-row">
                <div className="w-200 flex-shrink-0 text-wrap">
                  <FormItemInfo
                    labelId="privateNotesLabel"
                    label={locationDetails.organizational_options.custom_long_field_01.label}
                    descId="quote-request-detail.additionalInformation"
                    description={locationDetails.organizational_options.custom_long_field_01.additional_information}
                  />
                </div>
                <div>
                  <div className="w-392">
                    <Textarea
                      name={`organizational.${locationDetails.organizational_options.custom_long_field_01.field_id}`}
                      required={locationDetails.organizational_options.custom_long_field_01.mandatory}
                      placeholder="Add Private notes"
                      className="bg-white"
                      defaultValue={locationDetails.organizational_options.custom_long_field_01.initial_data}
                    />
                  </div>
                  {!!extractAllLinks(
                    watch(`organizational.${locationDetails.organizational_options.custom_long_field_01.field_id}`),
                  )?.length && (
                    <div className="pt-lg">
                      {extractAllLinks(
                        getValues(`organizational.${locationDetails.organizational_options.custom_long_field_01.field_id}`),
                      )?.map((link: string) => (
                        <div key={link}>{ReactHtmlParser(replaceLinks(link || "-"))}</div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );

  if (isConsolidated) {
    return (
      <FormSection>
        <FormHeader title="Organisation" />
        <div className="form-section-content">{renderOrganisationSection()}</div>
      </FormSection>
    );
  }

  return (
    <Card className="mb-3 form-container">
      <Accordion.Toggle as={Card.Header} className="accordion-header" eventKey={FormStepTypes.ORGANISATION}>
        <div className="d-flex align-items-center">
          {(hasError || state.draftSaved[FormStepTypes.ORGANISATION]) && (
            <div className={`valid-status ${hasError ? "error" : ""}`}>{hasError ? <ExclamationIcon /> : <CheckIcon />}</div>
          )}
          <h5>
            <FormattedMessage id="new-request.index.organisation" defaultMessage="Organisation" />
          </h5>
        </div>
        <SortIcon
          fill="#000"
          width={8}
          height={7}
          transform={`rotate(${currentActiveStep === FormStepTypes.ORGANISATION ? 180 : 0})`}
        />
      </Accordion.Toggle>
      <Accordion.Collapse eventKey={FormStepTypes.ORGANISATION}>
        <div style={{ padding: "1.25rem" }}>{renderOrganisationSection()}</div>
      </Accordion.Collapse>
    </Card>
  );
}

export default OrganisationForm;

OrganisationForm.propTypes = {
  disablePaymentLocation: PropTypes.bool,
};

OrganisationForm.defaultProps = {
  isMiniTender: false,
  validateSection: undefined,
  shouldValidateSection: undefined,
  isNewShipment: false,
  isLoadingShipmentBasic: false,
  isConsolidated: false,
  childRef: undefined,
  isShipment: false,
  disablePaymentLocation: false,
};
